import axios from 'axios';
import apiConfig from '../apiConfig';
import { getAccessToken } from '../../utils/jwt';
import { getAxiosInstance } from '../../utils/axiosApi';
import { transformToClientAccessList } from '../../utils/screenPermission';

const axiosInstance = getAxiosInstance({
  baseUrl: apiConfig.PortalAPIUrl,
});

axiosInstance.interceptors.request.use((config) => {
  config.headers = {
    Authorization: `Bearer ${getAccessToken()}`,
  };

  return config;
});

const searchInvoices = async ({
  invoiceIDText,
  invoiceNumber,
  locationID,
  clientID,
  sourceSystemID,
  jobIDText,
  jobName,
  invoiceType,
  invoiceDateFrom,
  invoiceDateTo,
  clientReference,
  paymentDueDate,
  paymentStatusID,
  jobCompletionDate,
  locationIDs,
  clientIDs,
  sourceSystemIDs,
  projects,
  isPetroHubUser,
  page,
  pageSize,
  sortBy,
}) => {
  const queryParams = [];
  const clientAccessFilter = transformToClientAccessList(clientIDs, [], []);
  if (invoiceIDText && invoiceIDText.trim() !== '') {
    queryParams.push(`invoiceIDText=${encodeURIComponent(invoiceIDText.trim())}`);
    queryParams.push('invoiceIDTextOP=Contains');
  }

  if (invoiceNumber && invoiceNumber.trim() !== '') {
    queryParams.push(`invoiceNumber=${encodeURIComponent(invoiceNumber.trim())}`);
    queryParams.push('invoiceNumberOP=Contains');
  }

  if (locationID) {
    queryParams.push(`locationID=${locationID}`);
  }

  if (clientID) {
    queryParams.push(`clientID=${clientID}`);
  }

  if (sourceSystemID) {
    queryParams.push(`sourceSystemID=${sourceSystemID}`);
  }

  if (jobIDText && jobIDText.trim() !== '') {
    queryParams.push(`jobIDText=${encodeURIComponent(jobIDText.trim())}`);
    queryParams.push('jobIDTextOP=Contains');
  }

  if (jobName && jobName.trim() !== '') {
    queryParams.push(`jobName=${encodeURIComponent(jobName.trim())}`);
    queryParams.push('jobNameOP=Contains');
  }

  if (invoiceType && invoiceType.trim() !== '') {
    queryParams.push(`invoiceType=${encodeURIComponent(invoiceType.trim())}`);
    queryParams.push('invoiceTypeOP=Contains');
  }

  if (invoiceDateFrom) {
    queryParams.push(`invoiceDateFrom=${encodeURIComponent(invoiceDateFrom)}`);
  }

  if (invoiceDateTo) {
    queryParams.push(`invoiceDateTo=${encodeURIComponent(invoiceDateTo)}`);
  }

  if (clientReference && clientReference.trim() !== '') {
    queryParams.push(`clientReference=${encodeURIComponent(clientReference.trim())}`);
    queryParams.push('clientReferenceOP=Contains');
  }

  if (paymentDueDate) {
    queryParams.push(`paymentDueDate=${encodeURIComponent(paymentDueDate)}`);
  }

  if (paymentStatusID) {
    queryParams.push(`paymentStatusID=${paymentStatusID}`);
  }

  if (jobCompletionDate) {
    queryParams.push(`jobCompletionDate=${encodeURIComponent(jobCompletionDate)}`);
  }

  if (Array.isArray(locationIDs) && locationIDs.length > 0) {
    const ids = locationIDs.join(',');
    queryParams.push(`locationIds=${ids}`);
  }

  if (Array.isArray(clientIDs) && clientIDs.length > 0 && !isPetroHubUser) {
    const ids = clientIDs.join(',');
    queryParams.push(`clientIds=${ids}`);
  }

  if (Array.isArray(sourceSystemIDs) && sourceSystemIDs.length > 0) {
    queryParams.push(`sourceSystemIds=${sourceSystemIDs.join(',')}`);
  }

  if (Array.isArray(projects) && projects.length > 0) {
    queryParams.push(`projects=${projects.map((x) => encodeURIComponent(x)).join(',')}`);
  }

  if (pageSize) {
    queryParams.push(`pageSize=${pageSize}`);
  }

  if (page) {
    queryParams.push(`currentPage=${page}`);
  }

  if (sortBy) {
    queryParams.push(`sortBy=${sortBy}`);
  }
  if (clientAccessFilter && isPetroHubUser) {
    queryParams.push(`clientAccessFilter=${encodeURIComponent(JSON.stringify(clientAccessFilter))}`);
  }
  try {
    const invoiceSearchUrl = isPetroHubUser ? `/Invoice/all/by/fuels?${queryParams.join('&')}` : `/invoice/all/by?${queryParams.join('&')}`;
    const response = await axiosInstance.post(invoiceSearchUrl);
    return response.data;
  } catch (e) {
    if (!axios.isCancel(e)) {
      throw e;
    }
  }

  return {};
};

const getInvoiceInfoByInvoiceId = async ({ invoiceId }) => {
  try {
    const response = await axiosInstance.post(`/Invoice/id/${invoiceId}`);
    return response.data;
  } catch (e) {
    throw new Error('Error getJobReportCountByJobIDs');
  }
};

const getInvoiceContentByInvoiceId = async ({ invoiceId }) => {
  try {
    const response = await axiosInstance.post(`/invoice/fileContent/invoiceId/${invoiceId}`, undefined, {
      responseType: 'blob',
    });
    return response.data;
  } catch (e) {
    throw new Error('Error getInvoiceContentByJobReportID');
  }
};

const getInvoiceDetailsByInvoiceId = async ({ invoiceId }) => {
  try {
    const response = await axiosInstance.post(`/invoice/invoicedetails/invoiceid/${invoiceId}`);
    return response.data;
  } catch (e) {
    throw new Error('Error getInvoiceDetailsByInvoiceId');
  }
};

const getInvoiceAttachedDocumentsByInvoiceId = async ({ invoiceId }) => {
  try {
    const response = await axiosInstance.post(`/Invoice/AttachedDocuments/invoiceid/${invoiceId}`);
    return response.data;
  } catch (e) {
    throw new Error('Error getInvoiceAttachedDocumentsByInvoiceId');
  }
};

const getInvoiceContentByManyInvoiceIDs = async ({ invoiceIds }) => {
  try {
    const response = await axiosInstance.post(
      '/Invoice/FileContent/InvoiceIDList',
      invoiceIds,
      {
        responseType: 'blob',
      }
    );

    return response.data;
  } catch (e) {
    throw new Error('Error getInvoiceContentByManyInvoiceIDs');
  }
};

const invoiceApi = {
  searchInvoices,
  getInvoiceInfoByInvoiceId,
  getInvoiceContentByInvoiceId,
  getInvoiceDetailsByInvoiceId,
  getInvoiceAttachedDocumentsByInvoiceId,
  getInvoiceContentByManyInvoiceIDs,
};

export default invoiceApi;
